import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, LoaderIcon, TruckIcon } from 'lucide-react';
import { builderPrices } from '../../data/builder';
import { BuilderApi } from '../../hooks/useBuilder';
import { protectionLabel } from '../../utils/builderCart';
import { money } from '../QuantityStepper';
import { SecureCheckoutNote } from '../SecureCheckoutNote';
import { UspsBadge } from '../UspsBadge';
interface BuilderSummaryProps {
  builder: BuilderApi;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function NotConnected({
  builder
}: BuilderSummaryProps) {
  if (!builder.notConnected) return null;
  return <div role="alert" className="border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-192" data-mp="3f37jxvfe91if">
      {builder.state.complete ? <>Part of this build isn&apos;t connected to checkout yet. Please try again shortly or email support@gritvuegear.com.</> : <>
          Essential checkout opens shortly. Want your X1 today?{' '}
          <button type="button" onClick={builder.upgradeToComplete} className="font-bold text-grit-500 underline underline-offset-2 hover:text-grit-400" data-id="element-193" data-mp="3krrjzfepcqvk">
            Switch to the Complete System — {money(builderPrices.complete)}
          </button>
        </>}
    </div>;
}
function CheckoutButton({
  builder,
  compact = false,
  onClick
}: BuilderSummaryProps & {
  compact?: boolean;
  onClick?: () => void;
}) {
  return <button type="button" onClick={onClick ?? builder.checkout} disabled={builder.routing} className={`inline-flex items-center justify-center gap-2 bg-grit-500 font-display font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite ${compact ? 'shrink-0 px-4 py-3.5 text-base' : 'w-full px-5 py-4 text-xl'}`} data-id="element-194" data-mp="2x1nu0aufspn1">
      {builder.routing ? 'Opening cart' : 'Continue'}
      {builder.routing ? <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-195" data-mp="28fpscsv61rj7" /> : <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-196" data-mp="1ujm6bmug4pht" />}
    </button>;
}

/** Desktop: persistent panel. Mirrors every selection and price in real time. */
export function BuilderSummary({
  builder
}: BuilderSummaryProps) {
  const {
    lines,
    total,
    state
  } = builder;
  return <aside aria-label="Your build" className="border border-white/10 bg-ink-900 p-5" data-id="element-197" data-mp="2r7sfmn2bc62f">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-steel-400" data-id="element-198" data-mp="2dg42cggbvbjv">Your X1</p>
      <ul className="mt-3 grid gap-2" aria-live="polite" data-id="element-199" data-mp="2iep4ilf545ay">
        <AnimatePresence initial={false} data-id="element-200" data-mp="365ujkyd9atk7">
          {lines.map(line => <motion.li key={line.label} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.18,
          ease: EASE
        }} className="flex items-start justify-between gap-3 overflow-hidden text-[14px]" data-id="element-201" data-mp="363hve3gwnmyp">
              <span className="flex items-start gap-2 text-offwhite" data-id="element-202" data-mp="3u0ef1zv4164h">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-203" data-mp="3p1jmw7b9657p" />
                {line.label}
                {line.quantity > 1 ? ` × ${line.quantity}` : ''}
              </span>
              <span className="shrink-0 tabular-nums text-steel-300" data-id="element-204" data-mp="2xbow067qy7gx">{money(line.price * line.quantity)}</span>
            </motion.li>)}
        </AnimatePresence>
        <li className="flex items-start justify-between gap-3 text-[13px] text-steel-400" data-id="element-205" data-mp="36blh5xuxgo78">
          <span data-id="element-206" data-mp="qjxi9wwcslls">{protectionLabel(state)}</span>
        </li>
      </ul>
      <div className="mt-4 flex items-end justify-between border-t border-white/10 pt-4" data-id="element-207" data-mp="qmabj67kaie5">
        <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-steel-400" data-id="element-208" data-mp="lkuqhe5dwbvr">Total</span>
        <span className="font-display text-4xl font-extrabold leading-none tabular-nums text-offwhite" data-id="element-209" data-mp="2rb7vt14ft1br">{money(total)}</span>
      </div>
      <p className="mt-2 flex items-center gap-2 text-[13px] font-semibold text-grit-500" data-id="element-210" data-mp="317swxbdc5sab">
        <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-211" data-mp="35965vubq6blg" />
        Free U.S. shipping
      </p>
      <div className="mt-4 grid gap-3" data-id="element-212" data-mp="3a88y2e6lwnc0">
        <CheckoutButton builder={builder} data-id="element-213" data-mp="3a77q4ikw0gyi" />
        <NotConnected builder={builder} data-id="element-214" data-mp="3a6ep7y8m0loa" />
      </div>
      <UspsBadge note="Free U.S. shipping." className="mt-4" data-id="element-215" data-mp="3f6aj34nlewq4" />
      <SecureCheckoutNote className="mt-4" data-id="element-216" data-mp="3jy2ebshp1qml" />
    </aside>;
}

/** Mobile: compact sticky bar, visible only while the builder is on screen. */
export function BuilderSummaryBar({
  builder,
  visible
}: BuilderSummaryProps & {
  visible: boolean;
}) {
  const {
    lines,
    total,
    state
  } = builder;
  const addOns = lines.length - 1;
  return <AnimatePresence data-id="element-217" data-mp="1th7f9xya2wq">
      {visible && <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      duration: 0.22,
      ease: EASE
    }} className="fixed inset-x-0 bottom-0 z-40 border-t border-grit-500/40 bg-ink-950/97 px-4 py-3 backdrop-blur lg:hidden" data-id="element-218" data-mp="1qcrjfb4hiq8">
          {builder.notConnected && <div className="mb-2" data-id="element-219" data-mp="18bgzpzi4yed9">
              <NotConnected builder={builder} data-id="element-220" data-mp="18cjnhyk5w0oy" />
            </div>}
          <div className="flex items-center justify-between gap-3" data-id="element-221" data-mp="24zoa1x1nh6c0">
            <div className="min-w-0" data-id="element-222" data-mp="20yum53o7jrj4">
              <p className="truncate text-[11px] font-bold uppercase tracking-wide text-steel-400" data-id="element-223" data-mp="20zwag9u8iais">
                {state.complete ? 'Complete System' : 'X1 Essential'}
                {addOns > 0 ? ` · ${addOns} add-on${addOns > 1 ? 's' : ''}` : ''} · Free U.S. shipping
              </p>
              <p className="font-display text-2xl font-extrabold leading-none tabular-nums text-offwhite" data-id="element-224" data-mp="2opqmwqan5x2i">
                <span className="text-base text-steel-300" data-id="element-225" data-mp="2jwnq89aki7i3">Your X1 — </span>
                {money(total)}
              </p>
            </div>
            <CheckoutButton builder={builder} compact onClick={builder.step === 'review' ? builder.checkout : () => {
          builder.goToStep('review');
          document.getElementById('builder-panel')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }} data-id="element-226" data-mp="37tkb290idwpg" />
          </div>
        </motion.div>}
    </AnimatePresence>;
}